/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo } from 'react';
import AACChromeAPI, { CurrentAccountInfo } from '@amzn/aac-chrome-api';
import { getCountryCodeByMarketplace } from 'src/commons/utils/advertising-config-store/ACSConfigClient';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';

function uniqBy<T, K extends keyof T>(
  array: T[],
  iteratee: K | ((item: T) => any),
): T[] {
  const seen: Record<string, boolean> = {};
  return array.filter((item) => {
    const key =
      typeof iteratee === 'function' ? iteratee(item) : item[iteratee];
    const keyString = String(key);

    if (!seen[keyString]) {
      seen[keyString] = true;
      return true;
    }

    return false;
  });
}

const ENTITY_TYPES = {
  VENDOR: 'BRAND',
  SELLER: 'SELLER_CENTRAL_ENTITY',
};

/**
 * A hook for fetching account information for the customer from
 * the AACChromeAPI. This is useful for filtering by countries that the
 * user has access to.
 */
const useAccountInfo = () => {
  const [accountInfo, setAccountInfo] = useState<CurrentAccountInfo>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState();
  const { config: internalChromeConfig } = useInternalChromeConfigContext();

  const {
    currentAccount,
    currentEntityType,
    globalAccountId,
    isAdmin,
    marketplaces,
  } = accountInfo;

  useEffect(() => {
    const fetchAccountInfo = async (): Promise<void> => {
      try {
        const result = await AACChromeAPI.getAccountInfo();
        setAccountInfo((result || {}) as CurrentAccountInfo);
      } catch (err: any) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAccountInfo();
  }, []);

  const { countryCodes, countryCodeToMarketplaceMap, selectedCountryCode } =
    useMemo<any>(() => {
      const codes: string[] = [];
      const marketplaceIdMap = new Map<string, any>();
      if (!marketplaces) {
        return {
          countryCodes: codes,
          countryCodeToMarketplaceIdMap: marketplaceIdMap,
        };
      }

      const finalMarketplaces = internalChromeConfig.isSpoofer
        ? uniqBy(marketplaces, 'marketplaceId' as any)
        : marketplaces;

      finalMarketplaces.forEach((marketplace) => {
        const code = getCountryCodeByMarketplace(marketplace.marketplaceId);
        codes.push(code);
        marketplaceIdMap.set(code, marketplace);
      });

      return {
        countryCodes: codes,
        countryCodeToMarketplaceMap: marketplaceIdMap,
        selectedCountryCode: getCountryCodeByMarketplace(
          currentAccount?.marketplaceId,
        ),
      };
    }, [currentAccount, marketplaces]);

  // Check if the customer can manage accounts.
  const canManageAccounts =
    currentEntityType === ENTITY_TYPES.VENDOR && isAdmin;

  return {
    countryCodes,
    countryCodeToMarketplaceMap,
    canManageAccounts,
    currentAccount,
    globalAccountId,
    isLoading,
    error,
    selectedCountryCode,
  };
};

export default useAccountInfo;
